import { useState, useEffect, useRef } from 'react';
import {
  Mail,
  MessageCircle,
  X,
  Send,
  Copy,
  Check,
  AlertCircle,
  ExternalLink,
  Phone,
  Globe,
  Loader2,
  ChevronDown,
  Eye,
  Code2,
  Pencil,
} from 'lucide-react';
import { Customer, CourseSession, EmailTemplate, WhatsAppTemplate } from '../../types/crm';
import { useToast } from '../../contexts/ToastContext';
import { EmailAutomationService } from '../../services/emailAutomationService';
import { supabaseEmailTemplateStorage, supabaseWhatsAppTemplateStorage } from '../../services/supabaseStorage';
import { PlaceholderService } from '../../services/placeholderService';
import { sessionReferenceCode, formatDeliveryModeLabel } from '../dashboard/trainingCalendarUtils';
import { SystemSettingsService } from '../../services/systemSettingsService';
import { ClientNotesService } from '../../services/clientNotesService';
import { findPreferredVariantTemplate } from '../../services/emailTemplateResolver';
import { courseEmailContentService } from '../../services/courseEmailContentService';
import { supabase } from '../../lib/supabase';
import { classifyRosterEmailKind, type RosterEmailKind } from '../../utils/rosterEmailIndicators';
import { EmailEditor } from '../ui/EmailEditor';

const DEFAULT_LOCATION_MATCH = '11c parkmore industrial estate';

function locationDetailsHaveAddress(loc: Record<string, unknown> | null | undefined): boolean {
  if (!loc || typeof loc !== 'object') return false;
  return [
    loc.addressLine1,
    loc.address,
    loc.eircodePostcode,
    loc.postcode,
    loc.name,
    loc.city,
  ].some((v) => String(v || '').trim().length > 0);
}

function locationRowToDetails(row: Record<string, unknown>): Record<string, unknown> {
  const eircode = String(row.eircode_postcode || row.postcode || row.eircodePostcode || '').trim();
  const addressLine1 = String(row.address_line_1 || row.address || row.addressLine1 || '').trim();
  const name = String(row.name || '').trim();
  return {
    id: row.id,
    name: name || undefined,
    addressLine1,
    addressLine2: String(row.address_line_2 || row.addressLine2 || '').trim(),
    addressLine3: String(row.address_line_3 || row.addressLine3 || '').trim(),
    city: String(row.city || '').trim(),
    countyState: String(row.county_state || row.county || row.countyState || '').trim(),
    eircodePostcode: eircode,
    country: String(row.country || '').trim() || 'Ireland',
    locationContactName: String(row.location_contact_name || row.contact_name || row.locationContactName || '').trim(),
    locationContactEmail: row.location_contact_email ?? row.contact_email ?? row.locationContactEmail ?? null,
    locationContactPhone: String(row.location_contact_phone || row.contact_phone || row.locationContactPhone || '').trim(),
    locationNotes: String(row.location_notes || row.notes || row.locationNotes || '').trim(),
    postcode: eircode,
    notes: String(row.location_notes || row.notes || row.locationNotes || '').trim(),
    address: String(row.address || row.address_line_1 || row.addressLine1 || '').trim(),
  };
}

function locationDetailsLabel(details: Record<string, unknown>): string {
  const eircode = String(details.eircodePostcode || details.postcode || '').trim();
  const addressParts = [
    details.addressLine1 || details.address,
    details.city,
    details.countyState,
    eircode,
    details.country,
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean);
  const addressLabel = addressParts.join(', ');
  const name = String(details.name || '').trim();
  return name ? (addressLabel ? `${name} - ${addressLabel}` : name) : addressLabel;
}

function mapsUrlFromLocationDetails(details: Record<string, unknown>): string {
  const eircode = String(details.eircodePostcode || details.postcode || '').trim();
  const addressLabel = [
    details.addressLine1 || details.address,
    details.city,
    eircode,
    details.country || 'Ireland',
  ]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join(', ');
  const query = eircode ? `${eircode}, Ireland` : addressLabel;
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '';
}

export interface CompanyCohortEmailContext {
  companyName: string;
  participantNames: string[];
  /** Named + virtual seats in the company group (drives {{ParticipantCount}}) */
  seatCount?: number;
  companyCustomer?: Customer;
  /** Roster attendee ids covered by a company cohort send (for Conf/Rem indicators). */
  attendeeIds?: string[];
}

export interface AttendeeContactModalProps {
  attendeeName: string;
  customer: Customer | undefined;
  session: CourseSession | undefined;
  sessionCourseName: string;
  sessionDates: string;
  sessionLocation?: string;
  /** Full location object so Eircode / Notes / Address placeholders work */
  sessionLocationDetails?: Record<string, unknown> | null;
  /** Sequential schedule index for TT##### (same as Bookings list) */
  sessionReferenceSequence?: number;
  sessionLocationMapsUrl?: string;
  sessionDeliveryMode?: string;
  attendeeCount?: number;
  enrollmentCourseFee?: number | null;
  amountPaid?: number | null;
  paymentStatus?: 'unpaid' | 'half_paid' | 'paid';
  actorUserId?: string;
  actorName?: string;
  initialTab?: 'email' | 'whatsapp';
  /** When set, send a single email covering all participants in the company group */
  companyCohort?: CompanyCohortEmailContext;
  /** Fired after queueEmail succeeds when the template classifies as booking conf or reminder. */
  onRosterEmailQueued?: (info: {
    kind: RosterEmailKind;
    recipientEmail: string;
    attendeeIds: string[];
    sentAt: string;
    sentBy?: string;
  }) => void;
  onClose: () => void;
}

const LANGUAGE_NAME_TO_CODE: Record<string, string> = {
  'polish': 'pl', 'croatian': 'hr', 'romanian': 'ro', 'lithuanian': 'lt',
  'latvian': 'lv', 'russian': 'ru', 'ukrainian': 'uk', 'portuguese': 'pt',
  'spanish': 'es', 'french': 'fr', 'german': 'de', 'italian': 'it',
  'dutch': 'nl', 'czech': 'cs', 'slovak': 'sk', 'hungarian': 'hu',
  'bulgarian': 'bg', 'greek': 'el', 'turkish': 'tr', 'arabic': 'ar',
  'chinese': 'zh', 'chinese (simplified)': 'zh', 'japanese': 'ja',
};

function formatCurrency(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '';
  return `€${Number(val).toFixed(2)}`;
}

function pickDefaultEmailTemplate(templates: EmailTemplate[]): string {
  const customerBookingTemplate = templates.find(
    (t) =>
      (t.name.toLowerCase().includes('booking') || t.name.toLowerCase().includes('confirmation')) &&
      !t.name.toLowerCase().includes('instructor')
  );
  const defaultTemplate = templates.find((t) => t.isDefault);
  if (customerBookingTemplate) return customerBookingTemplate.id;
  if (defaultTemplate) return defaultTemplate.id;
  return templates[0]?.id ?? '';
}

function parseEmailListInput(raw: string): string[] {
  return raw
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0 && email.includes('@'));
}

function participantPlaceholderValues(
  names: string[],
  mode: 'names' | 'count',
  seatCount?: number,
): { participantList: string; participantCount: string; attendeesList: string } {
  const count = seatCount ?? names.length;
  const participantCount = String(count);
  if (mode === 'count') {
    const label = count === 1 ? '1 person' : `${count} people`;
    return { participantList: label, participantCount, attendeesList: label };
  }
  const list = names.join(', ');
  return { participantList: list, participantCount, attendeesList: list };
}

function buildPlaceholderContext(
  attendeeName: string,
  customer: Customer | undefined,
  session: CourseSession | undefined,
  sessionCourseName: string,
  sessionDates: string,
  sessionLocation: string | undefined,
  sessionLocationDetails: Record<string, unknown> | null | undefined,
  sessionReferenceSequence: number | undefined,
  sessionLocationMapsUrl: string | undefined,
  attendeeCount: number | undefined,
  branding: { logoUrl: string; signatureUrl: string },
  enrollmentCourseFee: number | null | undefined,
  amountPaid: number | null | undefined,
  paymentStatus: 'unpaid' | 'half_paid' | 'paid' | undefined,
  companyCohort?: CompanyCohortEmailContext,
  participantListMode: 'names' | 'count' = 'names',
  courseEmailContent = '',
) {
  const firstBlock = session?.dateBlocks?.[0];
  const allDates = [
    ...new Set((session?.dateBlocks || []).map((b) => b.date).filter(Boolean) as string[]),
  ].sort((a, b) => a.localeCompare(b));

  const contactFirst = attendeeName.split(' ')[0] || attendeeName;
  const contactLast = attendeeName.split(' ').slice(1).join(' ') || '';

  const fee = enrollmentCourseFee != null ? Number(enrollmentCourseFee) : null;
  const paid = amountPaid != null ? Number(amountPaid) : null;
  const balance = fee != null && paid != null ? Math.max(0, fee - paid) : null;

  const courseLanguage = String(session?.language || '').trim();
  const locationObject = locationDetailsHaveAddress(sessionLocationDetails)
    ? sessionLocationDetails
    : undefined;
  const sequentialRef = session?.id
    ? sessionReferenceCode(session.id, sessionReferenceSequence)
    : '';
  const locationLabel = typeof sessionLocation === 'string' ? sessionLocation.trim() : '';
  const mapsUrl = sessionLocationMapsUrl || (locationObject ? mapsUrlFromLocationDetails(locationObject) : '');
  const mapsLinkHtml = mapsUrl
    ? `<a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:underline;">Get Directions</a>`
    : '';

  const cohortCompanyName = companyCohort?.companyName?.trim() || '';
  const cohortNames = companyCohort?.participantNames?.filter(Boolean) ?? [];
  const seatCount = companyCohort?.seatCount ?? (cohortNames.length > 0 ? cohortNames.length : undefined);
  const cohortPlaceholders = companyCohort
    ? participantPlaceholderValues(cohortNames, participantListMode, seatCount)
    : { participantList: '', participantCount: '', attendeesList: '' };
  const effectiveCompanyName =
    cohortCompanyName || customer?.companyName || `${customer?.firstName || ''} ${customer?.surname || ''}`.trim();

  return {
    contactName: attendeeName,
    contactFirstName: contactFirst,
    contactLastName: contactLast,
    courseName: sessionCourseName,
    courseLanguage,
    courseDate: firstBlock?.date || '',
    startTime: firstBlock?.startTime || '',
    endTime: firstBlock?.endTime || '',
    attendeeCount: seatCount ?? attendeeCount,
    customerName: effectiveCompanyName,
    companyName: effectiveCompanyName,
    // Prefer structured location so {{LocationEircode}} / {{LocationNotes}} / {{LocationAddress}} resolve
    location: locationObject || sessionLocation || '',
    locationAddress: locationLabel,
    locationEircode: String(
      (locationObject as { eircodePostcode?: string; postcode?: string } | undefined)?.eircodePostcode ||
        (locationObject as { postcode?: string } | undefined)?.postcode ||
        ''
    ).trim(),
    locationNotes: String(
      (locationObject as { locationNotes?: string; notes?: string } | undefined)?.locationNotes ||
        (locationObject as { notes?: string } | undefined)?.notes ||
        ''
    ).trim(),
    bookingData: {
      courseName: sessionCourseName,
      courseDate: firstBlock?.date || '',
      startTime: firstBlock?.startTime || '',
      endTime: firstBlock?.endTime || '',
      attendeeCount: seatCount ?? attendeeCount,
      courseLanguage,
      bookingReference: sequentialRef,
      courseSessionReference: sequentialRef,
      allBookingDates: allDates.length > 0 ? allDates : undefined,
      allBookingDatesWithTimes: session?.dateBlocks?.map((b) => ({
        date: b.date,
        startTime: b.startTime || '',
        endTime: b.endTime || '',
      })),
    },
    logoUrl: branding.logoUrl,
    signatureUrl: branding.signatureUrl,
    locationOrOnsite: locationLabel || sessionLocation || 'Onsite',
    locationAddressMultiline: undefined,
    locationMapsUrl: mapsUrl,
    // IMPORTANT: keep this as URL string (not HTML) so it is safe inside href="{{LocationMapsLink}}".
    locationMapsLink: mapsUrl,
    // Optional helper for templates that want a pre-built anchor snippet.
    locationMapsAnchor: mapsLinkHtml,
    courseFee: fee != null ? formatCurrency(fee) : '',
    amountPaid: paid != null ? formatCurrency(paid) : '',
    balanceDue: balance != null ? formatCurrency(balance) : '',
    paymentStatus: paymentStatus || '',
    courseSessionReference: sequentialRef,
    bookingReference: sequentialRef,
    customerId: customer?.id || '',
    participantList: cohortPlaceholders.participantList,
    participantCount: cohortPlaceholders.participantCount,
    attendeesList: cohortPlaceholders.attendeesList,
    notes: session?.notes || '',
    courseEmailContent,
  };
}

/** When templates use "{{StartTime}} - {{EndTime}}" and end time is empty, avoid "07:50 -" */
function cleanupOrphanTimeDash(html: string): string {
  return html.replace(
    /(\d{1,2}:\d{2})\s*-\s*(?=\s*(?:<\/td>|<\/p>|<br|<\/tr>|<\/div>|\r?\n|$))/gi,
    '$1',
  );
}

/** Strip HTML / entities for WhatsApp plain-text output */
function htmlToPlainWhatsApp(html: string): string {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n');
  const stripped = withBreaks.replace(/<[^>]+>/g, '');
  return stripped
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function normalisePhone(raw: string): string {
  let p = raw.replace(/[^\d+]/g, '');
  if (p.startsWith('00')) p = '+' + p.slice(2);
  if (p.startsWith('0') && !p.startsWith('+')) p = '+353' + p.slice(1);
  return p;
}

async function translateContent(
  supabaseUrl: string,
  supabaseAnonKey: string,
  html: string,
  subject: string,
  langCode: string,
): Promise<{ subject: string; body: string } | null> {
  const response = await fetch(`${supabaseUrl}/functions/v1/translate-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${supabaseAnonKey}`,
    },
    body: JSON.stringify({ html, subject, languages: [langCode] }),
  });
  const data = await response.json();
  const t = data.translations?.[0];
  if (t && !t.error) return { subject: t.subject || subject, body: t.body || html };
  return null;
}

function applyEmailTemplatePlaceholders(
  tpl: EmailTemplate,
  ctx: ReturnType<typeof buildPlaceholderContext>,
  resolvedLocationDetails: Record<string, unknown> | null | undefined,
  resolvedLocationLabel: string | undefined,
  resolvedMapsUrl: string | undefined,
): { subject: string; body: string } {
  const placeholders = PlaceholderService.generatePlaceholders(ctx as any);
  if (!locationDetailsHaveAddress(resolvedLocationDetails)) {
    placeholders['{{LocationOrOnsite}}'] = resolvedLocationLabel || 'Onsite';
    if (resolvedLocationLabel) {
      placeholders['{{LocationAddressMultiline}}'] = resolvedLocationLabel;
    }
  } else if (!placeholders['{{LocationOrOnsite}}']) {
    placeholders['{{LocationOrOnsite}}'] = resolvedLocationLabel || 'Onsite';
  }
  placeholders['{{LocationMapsUrl}}'] = ctx.locationMapsUrl || resolvedMapsUrl || '';
  placeholders['{{LocationMapsLink}}'] = ctx.locationMapsLink || resolvedMapsUrl || '';
  placeholders['{{LocationMapsAnchor}}'] = ctx.locationMapsAnchor;
  placeholders['{{CourseFee}}'] = ctx.courseFee;
  placeholders['{{AmountPaid}}'] = ctx.amountPaid;
  placeholders['{{BalanceDue}}'] = ctx.balanceDue;
  placeholders['{{PaymentStatus}}'] = ctx.paymentStatus;
  placeholders['{{Language}}'] = ctx.courseLanguage || placeholders['{{CourseLanguage}}'] || '';
  placeholders['{{CourseLanguage}}'] = ctx.courseLanguage || placeholders['{{CourseLanguage}}'] || '';
  placeholders['{{ReferenceNumber}}'] = ctx.bookingReference || placeholders['{{BookingReference}}'] || '';
  placeholders['{{ParticipantList}}'] = ctx.participantList || placeholders['{{ParticipantList}}'] || '';
  placeholders['{{ParticipantCount}}'] = ctx.participantCount || placeholders['{{ParticipantCount}}'] || '';
  placeholders['{{AttendeesList}}'] = ctx.attendeesList || placeholders['{{AttendeesList}}'] || '';

  const subject = PlaceholderService.replacePlaceholders(tpl.subject, placeholders as any);
  let body = PlaceholderService.replacePlaceholders(tpl.body, placeholders as any);
  body = cleanupOrphanTimeDash(body);
  if (!body.includes('<html') && !body.includes('<body') && !body.includes('<div')) {
    body = body.replace(/\n/g, '<br>');
  }
  return { subject, body };
}

export function AttendeeContactModal({
  attendeeName,
  customer,
  session,
  sessionCourseName,
  sessionDates,
  sessionLocation,
  sessionLocationDetails,
  sessionReferenceSequence,
  sessionLocationMapsUrl,
  sessionDeliveryMode,
  attendeeCount,
  enrollmentCourseFee,
  amountPaid,
  paymentStatus,
  actorUserId,
  actorName,
  initialTab = 'email',
  companyCohort,
  onRosterEmailQueued,
  onClose,
}: AttendeeContactModalProps) {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<'email' | 'whatsapp'>(initialTab);
  const [bodyMode, setBodyMode] = useState<'preview' | 'visual' | 'html'>('preview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [participantListMode, setParticipantListMode] = useState<'names' | 'count'>('names');

  const defaultRecipientEmail =
    companyCohort?.companyCustomer?.emailAddress?.trim() ||
    customer?.emailAddress?.trim() ||
    '';

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState<WhatsAppTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedWhatsappTemplateId, setSelectedWhatsappTemplateId] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [branding, setBranding] = useState({ logoUrl: '', signatureUrl: '' });
  const [resolvedLocationDetails, setResolvedLocationDetails] = useState<Record<string, unknown> | null | undefined>(
    sessionLocationDetails
  );
  const [resolvedLocationLabel, setResolvedLocationLabel] = useState<string | undefined>(sessionLocation);
  const [resolvedMapsUrl, setResolvedMapsUrl] = useState<string | undefined>(sessionLocationMapsUrl);
  const [courseEmailContent, setCourseEmailContent] = useState('');

  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [toEmail, setToEmail] = useState(defaultRecipientEmail);
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatedLanguage, setTranslatedLanguage] = useState<string | null>(null);

  const [whatsappMsg, setWhatsappMsg] = useState('');
  const [whatsappTranslated, setWhatsappTranslated] = useState(false);
  const [whatsappCopied, setWhatsappCopied] = useState(false);

  const translationDoneRef = useRef<Record<string, boolean>>({});
  const lastLoadedTemplateIdRef = useRef<string>('');
  const userEditedBodyRef = useRef(false);

  const recipientEmail = toEmail.trim();
  const recipientPhone = (customer?.contactPhoneNumber || '').trim().replace(/\s+/g, '');
  const whatsappPhone = normalisePhone(recipientPhone);
  const whatsappUrl = `whatsapp://send?phone=${encodeURIComponent(whatsappPhone)}&text=${encodeURIComponent(whatsappMsg)}`;
  const deliveryLabel = formatDeliveryModeLabel(sessionDeliveryMode);
  const isCompanyCohortMode = Boolean(companyCohort);
  const companySeatCount = companyCohort?.seatCount ?? companyCohort?.participantNames.length ?? 0;

  const prefLang = customer?.preferredLanguage?.trim();
  const langCode = prefLang ? LANGUAGE_NAME_TO_CODE[prefLang.toLowerCase()] : undefined;
  const showLanguageBadge = !!(prefLang && prefLang.toLowerCase() !== 'english' && langCode);

  useEffect(() => {
    setToEmail(defaultRecipientEmail);
  }, [defaultRecipientEmail]);

  useEffect(() => {
    EmailAutomationService.getSettings()
      .then((settings) => {
        if (settings.cc_recipients) setCcEmails(settings.cc_recipients);
        if (settings.bcc_recipients) setBccEmails(settings.bcc_recipients);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      supabaseEmailTemplateStorage.getEmailTemplates(),
      supabaseWhatsAppTemplateStorage.getWhatsAppTemplates(),
      SystemSettingsService.getSetting('company_logo_url'),
      SystemSettingsService.getSetting('email_signature_url'),
    ]).then(([loaded, waLoaded, logoUrl, sigUrl]) => {
      setTemplates(loaded);
      const defaultId = pickDefaultEmailTemplate(loaded);
      if (defaultId) setSelectedTemplateId(defaultId);
      setWhatsappTemplates(waLoaded);
      const waDef = waLoaded.find((t) => t.isDefault) || waLoaded[0];
      setSelectedWhatsappTemplateId(waDef?.id ?? '');
      setBranding({
        logoUrl: (logoUrl as string) || '',
        signatureUrl: (sigUrl as string) || '',
      });
    }).catch(() => {}).finally(() => setTemplatesLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const applyDetails = (details: Record<string, unknown>, label?: string, mapsUrl?: string) => {
      if (cancelled) return;
      setResolvedLocationDetails(details);
      setResolvedLocationLabel(label || locationDetailsLabel(details));
      setResolvedMapsUrl(mapsUrl || mapsUrlFromLocationDetails(details));
    };

    (async () => {
      if (locationDetailsHaveAddress(sessionLocationDetails)) {
        applyDetails(sessionLocationDetails as Record<string, unknown>, sessionLocation, sessionLocationMapsUrl);
        return;
      }

      const locationId = String(session?.locationId || '').trim();
      if (locationId) {
        const { data } = await supabase.from('locations').select('*').eq('id', locationId).maybeSingle();
        if (data && !cancelled) {
          applyDetails(locationRowToDetails(data as Record<string, unknown>));
          return;
        }
      }

      const { data: trainingRows } = await supabase
        .from('locations')
        .select('*')
        .eq('is_training_location', true);
      const rows = (trainingRows || []) as Record<string, unknown>[];
      const preferred =
        rows.find((row) =>
          `${row.name || ''} ${row.address_line_1 || ''} ${row.address || ''}`.toLowerCase().includes(DEFAULT_LOCATION_MATCH)
        ) ||
        rows.find((row) => String(row.name || '').toLowerCase().includes('ireland t&t')) ||
        rows[0];
      if (preferred && !cancelled) {
        applyDetails(locationRowToDetails(preferred));
      }
    })().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [session?.locationId, sessionLocationDetails, sessionLocation, sessionLocationMapsUrl]);

  useEffect(() => {
    let cancelled = false;
    const courseName = sessionCourseName?.trim() || session?.courseName?.trim() || '';
    if (!courseName) {
      setCourseEmailContent('');
      return;
    }
    const deliveryMode = sessionDeliveryMode || session?.deliveryMode || null;
    void courseEmailContentService
      .getCourseEmailContent(courseName, deliveryMode)
      .then((content) => {
        if (!cancelled) setCourseEmailContent(content);
      })
      .catch(() => {
        if (!cancelled) setCourseEmailContent('');
      });
    return () => {
      cancelled = true;
    };
  }, [sessionCourseName, session?.courseName, sessionDeliveryMode, session?.deliveryMode]);

  useEffect(() => {
    if (!selectedTemplateId || templatesLoading) {
      if (!templatesLoading && !selectedTemplateId) {
        setEmailSubject('');
        setEmailBody('');
        if (selectedTemplateId !== lastLoadedTemplateIdRef.current) {
          setTranslatedLanguage(null);
        }
      }
      return;
    }
    const selectedTpl = templates.find((t) => t.id === selectedTemplateId);
    const tpl = selectedTpl || undefined;
    if (!tpl) return;

    const templateChanged = selectedTemplateId !== lastLoadedTemplateIdRef.current;
    if (templateChanged) {
      userEditedBodyRef.current = false;
    }

    const ctx = buildPlaceholderContext(
      attendeeName, customer, session, sessionCourseName, sessionDates,
      resolvedLocationLabel, resolvedLocationDetails, sessionReferenceSequence, resolvedMapsUrl, attendeeCount, branding,
      enrollmentCourseFee, amountPaid, paymentStatus, companyCohort, participantListMode, courseEmailContent,
    );
    const { subject, body } = applyEmailTemplatePlaceholders(
      tpl, ctx, resolvedLocationDetails, resolvedLocationLabel, resolvedMapsUrl,
    );

    setEmailSubject(subject);
    if (!userEditedBodyRef.current || templateChanged) {
      setEmailBody(body);
    }

    if (templateChanged) {
      setTranslatedLanguage(null);
      lastLoadedTemplateIdRef.current = selectedTemplateId;
    }
  }, [
    selectedTemplateId, templates, branding, session, resolvedLocationDetails, sessionReferenceSequence,
    resolvedLocationLabel, resolvedMapsUrl, attendeeName, customer, sessionCourseName, attendeeCount,
    enrollmentCourseFee, amountPaid, paymentStatus, templatesLoading, companyCohort, participantListMode,
    courseEmailContent,
  ]);

  useEffect(() => {
    if (templatesLoading) return;

    const ctx = buildPlaceholderContext(
      attendeeName,
      customer,
      session,
      sessionCourseName,
      sessionDates,
      resolvedLocationLabel,
      resolvedLocationDetails,
      sessionReferenceSequence,
      resolvedMapsUrl,
      attendeeCount,
      branding,
      enrollmentCourseFee,
      amountPaid,
      paymentStatus,
      companyCohort,
      participantListMode,
      courseEmailContent,
    );
    const placeholders = PlaceholderService.generatePlaceholders(ctx as any);
    if (!locationDetailsHaveAddress(resolvedLocationDetails)) {
      placeholders['{{LocationOrOnsite}}'] = resolvedLocationLabel || 'Onsite';
      if (resolvedLocationLabel) {
        placeholders['{{LocationAddressMultiline}}'] = resolvedLocationLabel;
      }
    } else if (!placeholders['{{LocationOrOnsite}}']) {
      placeholders['{{LocationOrOnsite}}'] = resolvedLocationLabel || 'Onsite';
    }
    placeholders['{{LocationMapsUrl}}'] = ctx.locationMapsUrl || resolvedMapsUrl || '';
    placeholders['{{LocationMapsLink}}'] = ctx.locationMapsLink || resolvedMapsUrl || '';
    placeholders['{{LocationMapsAnchor}}'] = ctx.locationMapsAnchor;
    placeholders['{{CourseFee}}'] = ctx.courseFee;
    placeholders['{{AmountPaid}}'] = ctx.amountPaid;
    placeholders['{{BalanceDue}}'] = ctx.balanceDue;
    placeholders['{{PaymentStatus}}'] = ctx.paymentStatus;
    placeholders['{{Language}}'] = ctx.courseLanguage || placeholders['{{CourseLanguage}}'] || '';
    placeholders['{{CourseLanguage}}'] = ctx.courseLanguage || placeholders['{{CourseLanguage}}'] || '';
    placeholders['{{ReferenceNumber}}'] = ctx.bookingReference || placeholders['{{BookingReference}}'] || '';
    placeholders['{{ParticipantList}}'] = ctx.participantList || '';
    placeholders['{{ParticipantCount}}'] = ctx.participantCount || '';
    placeholders['{{AttendeesList}}'] = ctx.attendeesList || '';

    if (!selectedWhatsappTemplateId) {
      setWhatsappMsg('');
      return;
    }

    const tpl = whatsappTemplates.find((t) => t.id === selectedWhatsappTemplateId);
    if (!tpl) {
      setWhatsappMsg('');
      return;
    }

    let body = PlaceholderService.replacePlaceholders(tpl.body, placeholders as any);
    body = cleanupOrphanTimeDash(body);
    if (!body.includes('<html') && !body.includes('<body') && !body.includes('<div')) {
      body = body.replace(/\n/g, '<br>');
    }
    setWhatsappMsg(htmlToPlainWhatsApp(body));
  }, [
    templatesLoading,
    selectedWhatsappTemplateId,
    whatsappTemplates,
    branding,
    attendeeName,
    customer,
    session,
    sessionCourseName,
    sessionDates,
    resolvedLocationLabel,
    resolvedLocationDetails,
    resolvedMapsUrl,
    attendeeCount,
    enrollmentCourseFee,
    amountPaid,
    paymentStatus,
    companyCohort,
    participantListMode,
    courseEmailContent,
  ]);

  useEffect(() => {
    setWhatsappTranslated(false);
  }, [selectedWhatsappTemplateId]);

  const doEmailTranslation = async (
    subject: string,
    body: string,
    targetLangCode: string,
    templateId: string,
  ) => {
    const cacheKey = `email-${templateId}-${targetLangCode}`;
    if (translationDoneRef.current[cacheKey]) return;
    setIsTranslating(true);
    try {
      const result = await translateContent(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY,
        body,
        subject,
        targetLangCode,
      );
      if (result) {
        translationDoneRef.current[cacheKey] = true;
        setEmailSubject(result.subject);
        setEmailBody(cleanupOrphanTimeDash(result.body));
        setTranslatedLanguage(prefLang!);
      }
    } catch (err) {
      console.error('Auto-translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const doWhatsAppTranslation = async () => {
    if (!langCode || whatsappTranslated) return;
    setIsTranslating(true);
    try {
      const result = await translateContent(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY,
        whatsappMsg.replace(/\n/g, '<br>'),
        '',
        langCode,
      );
      if (result) {
        setWhatsappMsg(result.body.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ''));
        setWhatsappTranslated(true);
      }
    } catch (err) {
      console.error('WhatsApp translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      showError('No email address found for this recipient.');
      return;
    }
    setIsSending(true);
    try {
      const ccList = parseEmailListInput(ccEmails);
      const bccList = parseEmailListInput(bccEmails);
      const recipientLabel = isCompanyCohortMode
        ? companyCohort!.companyName
        : attendeeName;

      await EmailAutomationService.queueEmail({
        recipient_email: recipientEmail,
        recipient_name: recipientLabel,
        subject: emailSubject,
        html_content: emailBody,
        text_content: emailBody.replace(/<[^>]*>/g, ''),
        email_type: isCompanyCohortMode ? 'company_cohort_contact' : 'attendee_contact',
        related_booking_id: session?.id,
        related_customer_id: companyCohort?.companyCustomer?.id || customer?.id,
        cc_emails: ccList,
        bcc_emails: bccList,
        metadata: {
          sessionId: session?.id,
          courseSessionReference: session?.id
            ? sessionReferenceCode(session.id, sessionReferenceSequence)
            : '',
          bookingReference: session?.id
            ? sessionReferenceCode(session.id, sessionReferenceSequence)
            : '',
          sessionCourseName,
          sessionCourseLanguage: String(session?.language || '').trim() || undefined,
          sessionDates,
          sessionLocation,
          sentFrom: 'CourseSessionsPanel',
          translatedTo: translatedLanguage || undefined,
          templateId: selectedTemplateId || undefined,
          templateName: templates.find((t) => t.id === selectedTemplateId)?.name || undefined,
          cc_emails: ccList,
          bcc_emails: bccList,
          ...(isCompanyCohortMode
            ? {
                companyName: companyCohort!.companyName,
                participantNames: companyCohort!.participantNames,
                seatCount: companySeatCount,
                participantListMode,
                attendeeIds: companyCohort!.attendeeIds,
              }
            : {}),
        },
      });
      setEmailSent(true);
      setShowSendConfirm(false);
      const tplName = templates.find((t) => t.id === selectedTemplateId)?.name || 'template';
      showSuccess(
        isCompanyCohortMode
          ? `Email sent to ${companyCohort!.companyName} (via outbox to ${recipientEmail}).`
          : `Email sent to ${attendeeName} (via outbox to ${recipientEmail}).`
      );

      const kind = classifyRosterEmailKind({
        emailType: isCompanyCohortMode ? 'company_cohort_contact' : 'attendee_contact',
        subject: emailSubject,
        templateName: tplName,
      });
      if (kind && onRosterEmailQueued) {
        const attendeeIds = isCompanyCohortMode
          ? (companyCohort?.attendeeIds || []).filter(Boolean)
          : [];
        onRosterEmailQueued({
          kind,
          recipientEmail,
          attendeeIds,
          sentAt: new Date().toISOString(),
          sentBy: actorName,
        });
      }
      const payNote = buildPaymentNoteLine(enrollmentCourseFee, amountPaid);
      const sessionRef = session?.id
        ? sessionReferenceCode(session.id, sessionReferenceSequence)
        : '';
      const emailActivityMsg =
        `Communication: email sent to ${recipientEmail} (${tplName}) for ${recipientLabel}` +
        ` | Course: ${sessionCourseName}` +
        (sessionRef ? ` | Ref: ${sessionRef}` : '') +
        payNote +
        (translatedLanguage ? ` | Translated to ${translatedLanguage}` : '') +
        (isCompanyCohortMode ? ` | Company cohort (${companySeatCount} seats)` : '');
      const noteCustomerId = companyCohort?.companyCustomer?.id || customer?.id;
      if (noteCustomerId) {
        ClientNotesService.logClientTrainingUpdate(
          noteCustomerId,
          { courseName: sessionCourseName, datesSummary: sessionDates },
          emailActivityMsg,
          actorUserId,
          actorName
        ).catch(() => {});
      }
    } catch (err: any) {
      showError(err.message || 'Failed to send email.');
    } finally {
      setIsSending(false);
    }
  };

  const handleToggleEditMode = () => {
    setIsEditMode((prev) => {
      const next = !prev;
      if (next) setBodyMode('visual');
      else setBodyMode('preview');
      return next;
    });
  };

  const handleEmailBodyChange = (value: string) => {
    userEditedBodyRef.current = true;
    setEmailBody(value);
  };

  const handleOpenWhatsApp = () => {
    if (!whatsappPhone) {
      showError('No phone number found for this attendee.');
      return;
    }
    navigator.clipboard.writeText(whatsappMsg).catch(() => {});
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

    const waTpl = selectedWhatsappTemplateId
      ? whatsappTemplates.find((t) => t.id === selectedWhatsappTemplateId)?.name
      : 'Built-in reminder';
    const payNoteWa = buildPaymentNoteLine(enrollmentCourseFee, amountPaid);
    const sessionRef = session?.id
      ? sessionReferenceCode(session.id, sessionReferenceSequence)
      : '';
    const waOpenMsg =
      `Communication: WhatsApp opened for ${attendeeName} (${whatsappPhone}) using ${waTpl}` +
      ` | Course: ${sessionCourseName}` +
      (sessionRef ? ` | Ref: ${sessionRef}` : '') +
      payNoteWa;
    if (customer?.id) {
      ClientNotesService.logClientTrainingUpdate(
        customer.id,
        { courseName: sessionCourseName, datesSummary: sessionDates },
        waOpenMsg,
        actorUserId,
        actorName
      ).catch(() => {});
    }
  };

  const handleCopyWhatsApp = async () => {
    try {
      await navigator.clipboard.writeText(whatsappMsg);
      setWhatsappCopied(true);
      setTimeout(() => setWhatsappCopied(false), 2000);

      const waTpl = selectedWhatsappTemplateId
        ? whatsappTemplates.find((t) => t.id === selectedWhatsappTemplateId)?.name
        : 'Built-in reminder';
      const payNoteCopy = buildPaymentNoteLine(enrollmentCourseFee, amountPaid);
      const sessionRef = session?.id
        ? sessionReferenceCode(session.id, sessionReferenceSequence)
        : '';
      const waCopyMsg =
        `Communication: WhatsApp message copied for ${attendeeName} (${whatsappPhone}) using ${waTpl}` +
        ` | Course: ${sessionCourseName}` +
        (sessionRef ? ` | Ref: ${sessionRef}` : '') +
        payNoteCopy;
      if (customer?.id) {
        ClientNotesService.logClientTrainingUpdate(
          customer.id,
          { courseName: sessionCourseName, datesSummary: sessionDates },
          waCopyMsg,
          actorUserId,
          actorName
        ).catch(() => {});
      }
    } catch {
      showError('Failed to copy message.');
    }
  };

  return (
    <>
    {showSendConfirm ? (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-3">
        <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl">
          <p className="text-base font-semibold text-gray-900">Send this email?</p>
          <p className="mt-1 text-sm text-gray-600">
            {isCompanyCohortMode
              ? `Send to ${recipientEmail} for ${companyCohort!.companyName}?`
              : `Send to ${recipientEmail}?`}
          </p>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              onClick={() => setShowSendConfirm(false)}
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={() => void handleSendEmail()}
              disabled={isSending}
            >
              {isSending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    ) : null}
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-2 backdrop-blur-sm">
      <div
        className="flex w-[98vw] max-w-[1600px] flex-col rounded-2xl border border-gray-200 bg-white shadow-2xl"
        style={{ height: 'calc(100vh - 1rem)', maxHeight: 'calc(100vh - 1rem)' }}
      >

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-800 text-sm font-bold text-white">
              {isCompanyCohortMode
                ? companyCohort!.companyName.slice(0, 2).toUpperCase()
                : attendeeName.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {isCompanyCohortMode ? companyCohort!.companyName : attendeeName}
              </p>
              <p className="text-xs text-gray-500 flex flex-wrap items-center gap-1.5">
                <span>{sessionCourseName} · {sessionDates}</span>
                {deliveryLabel ? (
                  <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                    {deliveryLabel}
                  </span>
                ) : null}
                {isCompanyCohortMode ? (
                  <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
                    {companySeatCount} seat{companySeatCount === 1 ? '' : 's'}
                  </span>
                ) : null}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Contact info strip */}
        <div className="flex shrink-0 flex-wrap gap-4 border-b border-gray-100 bg-gray-50 px-6 py-2.5 text-xs text-gray-600">
          {!isCompanyCohortMode && (
            <>
              <span className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-gray-400" />
                {recipientPhone || <span className="italic text-gray-400">No phone</span>}
              </span>
              {showLanguageBadge && (
                <span className="flex items-center gap-1.5 text-blue-600">
                  <Globe className="h-3.5 w-3.5" />
                  Prefers {prefLang}
                </span>
              )}
            </>
          )}
          {isCompanyCohortMode && (
            <span className="text-gray-600">
              {companyCohort!.participantNames.length > 0
                ? `Participants: ${companyCohort!.participantNames.join(', ')}`
                : 'No participant names yet'}
            </span>
          )}
        </div>

        {/* Channel tabs */}
        <div className="flex shrink-0 border-b border-gray-100">
          <button
            onClick={() => setActiveTab('email')}
            className={`flex flex-1 items-center justify-center gap-2 py-3 text-sm font-medium transition ${
              activeTab === 'email' ? 'border-b-2 border-blue-600 text-blue-700' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Mail className="h-4 w-4" />Email
          </button>
          {!isCompanyCohortMode && (
            <button
              onClick={() => setActiveTab('whatsapp')}
              className={`flex flex-1 items-center justify-center gap-2 py-3 text-sm font-medium transition ${
                activeTab === 'whatsapp' ? 'border-b-2 border-emerald-600 text-emerald-700' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageCircle className="h-4 w-4" />WhatsApp
            </button>
          )}
        </div>

        {/* Email top action bar */}
        {activeTab === 'email' && (
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-4 py-2.5">
            {showLanguageBadge && (
              <button
                onClick={() => langCode && doEmailTranslation(emailSubject, emailBody, langCode, selectedTemplateId || 'manual')}
                disabled={isTranslating || !langCode || !emailBody.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isTranslating ? (
                  <><Loader2 className="h-4 w-4 animate-spin" />Translating...</>
                ) : (
                  <><Globe className="h-4 w-4" />Translate email</>
                )}
              </button>
            )}
            <button
              onClick={handleToggleEditMode}
              className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-semibold transition ${
                isEditMode
                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Pencil className="h-4 w-4" />
              {isEditMode ? 'Editing' : 'Edit email'}
            </button>
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <div className="flex-1" />
            <button
              onClick={() => setShowSendConfirm(true)}
              disabled={isSending || emailSent || !recipientEmail || !emailSubject.trim() || !selectedTemplateId}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold transition ${
                emailSent
                  ? 'bg-emerald-100 text-emerald-700 cursor-default'
                  : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {emailSent ? (
                <><Check className="h-4 w-4" />Sent</>
              ) : isSending ? (
                <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />Sending...</>
              ) : (
                <><Send className="h-4 w-4" />Send Email</>
              )}
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {activeTab === 'email' ? (
            <div className="flex min-h-0 flex-1 flex-col gap-0 lg:flex-row">

              {/* Left panel — controls */}
              <div className="flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-b border-gray-100 p-5 lg:w-96 lg:border-b-0 lg:border-r">
                {!recipientEmail && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    No email address on record. Enter a recipient below or add one in CRM.
                  </div>
                )}

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">To</label>
                  <input
                    type="email"
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="recipient@example.com"
                  />
                </div>

                {/* Template selector */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">Email Template</label>
                  {templatesLoading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading templates...
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="text-xs text-amber-600">No templates found. Create one in Email Templates.</p>
                  ) : (
                    <div className="relative">
                      <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        disabled={templatesLoading}
                        className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60"
                      >
                        {!selectedTemplateId ? (
                          <option value="">Select a template...</option>
                        ) : null}
                        {templates.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}{t.isDefault ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    </div>
                  )}
                </div>

                {isCompanyCohortMode && (
                  <div>
                    <label className="mb-1.5 block text-xs font-medium text-gray-600">Participant wording</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setParticipantListMode('names')}
                        className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition ${
                          participantListMode === 'names'
                            ? 'border-violet-400 bg-violet-50 text-violet-800'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        List names
                      </button>
                      <button
                        type="button"
                        onClick={() => setParticipantListMode('count')}
                        className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition ${
                          participantListMode === 'count'
                            ? 'border-violet-400 bg-violet-50 text-violet-800'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        Count only
                      </button>
                    </div>
                  </div>
                )}

                {/* Translation status */}
                {showLanguageBadge && selectedTemplateId && (
                  <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                    isTranslating
                      ? 'border border-blue-200 bg-blue-50 text-blue-700'
                      : translatedLanguage
                      ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border border-blue-200 bg-blue-50 text-blue-700'
                  }`}>
                    {isTranslating ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />Translating to {prefLang}...</>
                    ) : translatedLanguage ? (
                      <><Globe className="h-3.5 w-3.5 shrink-0" />Translated to {translatedLanguage}</>
                    ) : (
                      <><Globe className="h-3.5 w-3.5 shrink-0" />Use Translate email above for {prefLang}</>
                    )}
                  </div>
                )}

                {/* Subject */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">CC</label>
                  <input
                    type="text"
                    value={ccEmails}
                    onChange={(e) => setCcEmails(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600">BCC</label>
                  <input
                    type="text"
                    value={bccEmails}
                    onChange={(e) => setBccEmails(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="admin@example.com"
                  />
                </div>

                <p className="text-[11px] text-gray-400 mt-auto">
                  Queued emails appear in Admin → Email Outbox. Approve and send from there.
                </p>
              </div>

              {/* Right panel — preview / visual editor / advanced HTML */}
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex shrink-0 items-center gap-1 border-b border-gray-100 bg-gray-50 px-4 py-2">
                  <button
                    type="button"
                    onClick={() => { setBodyMode('preview'); setIsEditMode(false); }}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                      bodyMode === 'preview'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Eye className="h-3.5 w-3.5" />Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBodyMode('visual'); setIsEditMode(true); }}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                      bodyMode === 'visual'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Pencil className="h-3.5 w-3.5" />Edit text
                  </button>
                  <button
                    type="button"
                    onClick={() => { setBodyMode('html'); setIsEditMode(true); }}
                    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                      bodyMode === 'html'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                    title="Advanced: raw HTML for this send only"
                  >
                    <Code2 className="h-3.5 w-3.5" />HTML
                  </button>
                  {bodyMode !== 'preview' && (
                    <span className="ml-2 text-[11px] text-gray-400">
                      Edits apply to this send only, not the saved template.
                    </span>
                  )}
                  {isTranslating && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-blue-600">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />Translating...
                    </span>
                  )}
                </div>

                {bodyMode === 'preview' ? (
                  <div className="flex-1 min-h-[50vh] overflow-y-auto bg-gray-100 p-4">
                    {emailBody ? (
                      <div className="w-full rounded-xl border border-gray-200 bg-white shadow-sm">
                        <div className="border-b border-gray-100 px-5 py-3">
                          <p className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">To: </span>
                            {recipientEmail || <span className="italic text-gray-400">no email</span>}
                          </p>
                          {ccEmails.trim() ? (
                            <p className="mt-0.5 text-xs text-gray-500">
                              <span className="font-medium text-gray-700">CC: </span>{ccEmails}
                            </p>
                          ) : null}
                          {bccEmails.trim() ? (
                            <p className="mt-0.5 text-xs text-gray-500">
                              <span className="font-medium text-gray-700">BCC: </span>{bccEmails}
                            </p>
                          ) : null}
                          <p className="mt-0.5 text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Subject: </span>
                            {emailSubject || <span className="italic text-gray-400">no subject</span>}
                          </p>
                        </div>
                        <div
                          className="prose prose-sm max-w-none p-5 text-gray-800"
                          dangerouslySetInnerHTML={{ __html: emailBody }}
                        />
                      </div>
                    ) : (
                      <div className="flex h-full min-h-48 items-center justify-center text-sm text-gray-400">
                        Select a template to preview the email
                      </div>
                    )}
                  </div>
                ) : bodyMode === 'visual' ? (
                  <div className="flex min-h-[50vh] flex-1 flex-col overflow-y-auto bg-white p-3">
                    <EmailEditor
                      content={emailBody}
                      onChange={handleEmailBodyChange}
                      placeholder="Edit the email text for this send…"
                      height={420}
                    />
                  </div>
                ) : (
                  <textarea
                    value={emailBody}
                    onChange={(e) => handleEmailBodyChange(e.target.value)}
                    className="min-h-[50vh] flex-1 resize-none bg-gray-900 p-4 font-mono text-xs text-green-300 focus:outline-none"
                    placeholder="HTML content will appear here once a template is selected..."
                    spellCheck={false}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4 p-6">
              {!recipientPhone && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  No phone number on record. Add one in CRM first.
                </div>
              )}

              {recipientPhone && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm">
                  <span className="text-xs font-medium text-gray-500 mr-2">Will message:</span>
                  <span className="font-semibold text-gray-800">{whatsappPhone}</span>
                </div>
              )}

              {/* WhatsApp template selector */}
              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">WhatsApp Template</label>
                {templatesLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading templates...
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedWhatsappTemplateId}
                      onChange={(e) => setSelectedWhatsappTemplateId(e.target.value)}
                      className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-900 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    >
                      <option value="">Select a template...</option>
                      {whatsappTemplates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.isDefault ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                )}
                {!templatesLoading && whatsappTemplates.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">No saved templates. Add one under Dashboard → WhatsApp Templates (use {{Placeholders}} syntax).</p>
                )}
              </div>

              {showLanguageBadge && (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                  isTranslating
                    ? 'border border-blue-200 bg-blue-50 text-blue-700'
                    : whatsappTranslated
                    ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                    : 'border border-blue-200 bg-blue-50 text-blue-700'
                }`}>
                  {isTranslating ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />Translating to {prefLang}...</>
                  ) : whatsappTranslated ? (
                    <><Globe className="h-3.5 w-3.5 shrink-0" />Translated to {prefLang}</>
                  ) : (
                    <><Globe className="h-3.5 w-3.5 shrink-0" />Click Translate to convert to {prefLang}</>
                  )}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-600">Message</label>
                <textarea
                  rows={14}
                  value={whatsappMsg}
                  onChange={(e) => setWhatsappMsg(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 leading-relaxed focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                {showLanguageBadge && (
                  <button
                    onClick={doWhatsAppTranslation}
                    disabled={isTranslating || !langCode || !whatsappMsg.trim()}
                    className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 transition"
                  >
                    {isTranslating ? (
                      <><Loader2 className="h-4 w-4 animate-spin" />Translating...</>
                    ) : (
                      <><Globe className="h-4 w-4" />Translate message</>
                    )}
                  </button>
                )}
                <button
                  onClick={handleCopyWhatsApp}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  {whatsappCopied ? (
                    <><Check className="h-4 w-4 text-emerald-500" />Copied</>
                  ) : (
                    <><Copy className="h-4 w-4" />Copy Message</>
                  )}
                </button>
                <button
                  onClick={handleOpenWhatsApp}
                  disabled={!recipientPhone}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <ExternalLink className="h-4 w-4" />
                  Open WhatsApp
                </button>
              </div>

              <p className="text-[11px] text-gray-400 text-center">
                Opens the WhatsApp desktop app with the message pre-filled. Also copied to clipboard as a backup.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

function buildPaymentNoteLine(
  enrollmentCourseFee: number | null | undefined,
  amountPaid: number | null | undefined,
): string {
  const fee = enrollmentCourseFee != null ? Number(enrollmentCourseFee) : null;
  const paid = amountPaid != null ? Number(amountPaid) : null;
  if (fee == null) return '';
  const balance = paid != null ? Math.max(0, fee - paid) : null;
  let line = ` | Fee: ${formatCurrency(fee)}`;
  if (paid != null) line += ` | Paid: ${formatCurrency(paid)}`;
  if (balance != null && balance > 0) line += ` | Balance due: ${formatCurrency(balance)}`;
  return line;
}